import React, { useState } from 'react';
import { isEmpty, findKey, findIndex } from 'lodash';
import { useSelector } from 'react-redux';

import styles from './CustomDistributionMethod.module.scss';

import DestinationOption from '../../components/DestinationOption/DestinationOption';
import HelpBox from '../../components/HelpBox/HelpBox';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import config from '../../config/config';
import ProgressBar from '../../components/Progress/ProgressBar/ProgressBar';
import ProgressButtons from '../../components/Progress/ProgressButtons/ProgressButtons';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import CustomDistributionMethodLockedFunds from './CustomDistributionMethodLockedFunds';
import FundDisplayText from '../ProportionateDistributionMethod/FundDisplayText';
import {
  getRetirementFundsCount,
  getRMDfunds,
  getBalanceAsOfDate,
} from '../../redux/modules/UserFunds/userFunds.selectors';
import {
  CUSTOM_DISTRIBUTION_METHOD_STEP,
  REINVEST_STEP,
  FREQUENCY_STEP,
  DISTRIBUTION_METHOD_STEP,
  TOLL_FREE_NUMBER,
  ADDRESS_STEP,
  BANK_STEP,
} from '../../settings/settings';
import { getTotalRMDAmount } from '../../redux/modules/RMD/RMDSetting.selectors';
import {
  getSelectedDestination,
  getSelectedDistributionFunds,
} from '../../redux/modules/shoppingCart/shoppingCart.selectors';
import alertIcon from '../../assets/images/alert.svg';

const CustomDistributionMethod = (props) => {
  const fundCount = useSelector(getRetirementFundsCount);
  const rmdFunds = useSelector(getRMDfunds);
  const {
    ROLLOVER_IRA: {
      lockedFunds: rolloverLockedFunds,
      unlockedFunds: rolloverUnlockedFunds,
    },
    TRADITIONAL_IRA: {
      lockedFunds: traditionalLockedFunds,
      unlockedFunds: traditionalUnlockedFunds,
    },
  } = rmdFunds;

  const rolloverUnlockedForCustom = rolloverUnlockedFunds;
  const traditionalUnlockedForCustom = traditionalUnlockedFunds;

  const lockedFundCount =
    rolloverLockedFunds.length + traditionalLockedFunds.length;

  const totalRMD = useSelector(getTotalRMDAmount);
  const currentVals = useSelector(getSelectedDistributionFunds);
  const balanceAsOfDate = useSelector(getBalanceAsOfDate);

  const [rolloverVal, setRolloverVal] = useState(currentVals.rolloverFunds);

  const [traditionalVal, setTraditional] = useState(
    currentVals.traditionalFunds
  );

  const [checkForLockedFunds, setCheckForLockedFunds] = useState(true);
  const changeRollOver = (value, index, selectedFund) => {
    const num = Number(value);
    const fundIndex = findIndex(
      rolloverUnlockedForCustom,
      (fund) => selectedFund.agreementID === fund.agreementID
    );
    const rolloverTemp = {
      ...rolloverVal,
      [index]: {
        ...rolloverUnlockedForCustom[fundIndex],
        value: num,
      },
    };
    setRolloverVal(rolloverTemp);
  };

  const changeTraditional = (value, index, selectedFund) => {
    const num = Number(value);
    const fundIndex = findIndex(
      traditionalUnlockedForCustom,
      (fund) => selectedFund.agreementID === fund.agreementID
    );
    const traditionalTemp = {
      ...traditionalVal,
      [index]: {
        ...traditionalUnlockedForCustom[fundIndex],
        value: num,
      },
    };
    setTraditional(traditionalTemp);
  };

  const checkPercent = () => {
    const rollErrorList = {};
    const traditinalErrorList = {};
    let bool = false;
    let rolNum = 0;
    let tradNum = 0;
    for (const key in rolloverVal) {
      const num = rolloverVal[key].value;
      const element = rolloverVal[key];
      if (element) {
        rollErrorList[key] =
          num > 0 ? (totalRMD * num) / 100 > element.equityAsOf : false;
        bool = bool || rollErrorList[key];
      }
      rolNum = rolNum + num * 100;
    }
    for (const key in traditionalVal) {
      const num = traditionalVal[key].value;
      const element = traditionalVal[key];
      if (element) {
        traditinalErrorList[key] =
          num > 0 ? (totalRMD * num) / 100 > element.equityAsOf : false;
        bool = bool || traditinalErrorList[key];
      }
      tradNum = tradNum + num * 100;
    }
    const isNull = isEmpty(rolloverVal) && isEmpty(traditionalVal);
    return {
      hasError: isNull ? false : (rolNum + tradNum) / 100 !== 100,
      isDisabled: !((rolNum + tradNum) / 100 === 100) || bool,
      rollErrorList,
      traditinalErrorList,
    };
  };

  const checkPercentObj = checkPercent();
  const selectedDestination = useSelector(getSelectedDestination);

  return (
    <div className="rmd-distribution-wrapper">
      <div className="sticky-header">
        <div className="heading">Auto-RMD Setup</div>
        <ProgressButtons
          edit={props.edit}
          currentStep={CUSTOM_DISTRIBUTION_METHOD_STEP}
          previousStep={DISTRIBUTION_METHOD_STEP}
          currentSelection={currentVals}
          payload={{
            traditionalFunds: traditionalVal,
            rolloverFunds: rolloverVal,
          }}
          nextStep={
            selectedDestination === BANK_STEP ||
            selectedDestination === ADDRESS_STEP
              ? FREQUENCY_STEP
              : REINVEST_STEP
          }
          {...props}
          disabled={checkPercentObj.isDisabled || lockedFundCount.length > 0}
        >
          <ProgressBar completedSteps={[0]} activeIndex={1} />
        </ProgressButtons>
      </div>
      <PageWrapper step={CUSTOM_DISTRIBUTION_METHOD_STEP} isHelpCol={true}>
        <div className="scroll-wrapper">
          <div className="reinvest-title">
            How much of each investment should make up your RMD?
          </div>
          <div className={styles['option-container']}>
            {rolloverUnlockedForCustom.length === 0 &&
              traditionalUnlockedForCustom.length === 0 && (
                <div className="no-funds">No Eligible Investments</div>
              )}
            {(rolloverUnlockedForCustom.length > 0 ||
              traditionalUnlockedForCustom.length > 0) && (
              <div className={styles['fund-list']}>
                {rolloverUnlockedForCustom.length > 0 && (
                  <>
                    <div className={styles['sub-title']}> {'Rollover IRA'}</div>
                    {rolloverUnlockedForCustom.map((fund, index) => {
                      const existingIndex = findKey(
                        rolloverVal,
                        (temp) => temp.agreementID === fund.agreementID
                      );
                      const fundIndex =
                        existingIndex > -1 ? existingIndex : fund.agreementID;
                      return (
                        <DestinationOption
                          key={index}
                          optionText={
                            <FundDisplayText
                              retirement={true}
                              custom={true}
                              value={
                                rolloverVal && rolloverVal[fundIndex]
                                  ? rolloverVal[fundIndex].value
                                  : 0
                              }
                              balanceFund={
                                checkPercentObj.rollErrorList[fundIndex]
                              }
                              fund={fund}
                              hasError={checkPercentObj.hasError}
                              handleValueChange={(value) =>
                                changeRollOver(value, fundIndex, fund)
                              }
                              balanceAsOfDate={balanceAsOfDate}
                            />
                          }
                          rollLastFund={
                            rolloverUnlockedForCustom.length - 1 === fundIndex
                          }
                          value={1}
                          reinvest={true}
                          customFunds={true}
                        />
                      );
                    })}
                  </>
                )}
                {traditionalUnlockedForCustom.length > 0 && (
                  <>
                    <div className={styles['sub-title']}>
                      {' '}
                      {'Traditional IRA'}
                    </div>
                    {traditionalUnlockedForCustom.map((fund, index) => {
                      const existingIndex = findKey(
                        traditionalVal,
                        (temp) => temp.agreementID === fund.agreementID
                      );
                      const fundIndex =
                        existingIndex > -1 ? existingIndex : fund.agreementID;

                      return (
                        <DestinationOption
                          key={index}
                          optionText={
                            <FundDisplayText
                              custom={true}
                              value={
                                traditionalVal && traditionalVal[fundIndex]
                                  ? traditionalVal[fundIndex].value
                                  : 0
                              }
                              balanceFund={
                                checkPercentObj.traditinalErrorList[fundIndex]
                              }
                              fund={fund}
                              hasError={checkPercentObj.hasError}
                              handleValueChange={(value) =>
                                changeTraditional(value, fundIndex, fund)
                              }
                              balanceAsOfDate={balanceAsOfDate}
                            />
                          }
                          tradLastFund={
                            traditionalUnlockedForCustom.length - 1 ===
                            fundIndex
                          }
                          value={1}
                          reinvest={true}
                          customFunds={true}
                        />
                      );
                    })}
                  </>
                )}
                <div
                  className={`${styles['totalValue']} ${
                    checkPercentObj.hasError && styles['hasError']
                  }`}
                >
                  Must equal 100%
                </div>
              </div>
            )}
            {checkForLockedFunds && lockedFundCount > 0 && (
              <div>
                <div className={styles['warning-banner']}>
                  <img
                    className={styles['lockedcard-img']}
                    src={alertIcon}
                    alt="alert icon"
                  />
                  <h3 className={styles['lockedcard-title']}>
                    Ineligible Accounts
                  </h3>
                  <div className={styles['lockedcard-desc']}>
                    A restriction has been placed on your account(s).{' '}
                    <a
                      className={styles['error-link']}
                      rel="noopener noreferrer"
                      href={`${config.url}/personal-investing/help/contact/index.html`}
                      target="_blank"
                    >
                      Contact us
                    </a>{' '}
                    for more information.
                  </div>
                </div>
                {/* <div className={styles['locked-div']}>
                  <button
                    id="show-all-retirement-funds"
                    className={styles['lockedcount']}
                    onClick={() => setCheckForLockedFunds(true)}
                  >
                    {lockedFundCount} out of {fundCount} investments
                  </button>{' '}
                  <span className={styles['lockedcount-text']}>
                    are not available
                  </span>
                </div> */}
              </div>
            )}
            {checkForLockedFunds && <CustomDistributionMethodLockedFunds />}
            <HelpBox step={DISTRIBUTION_METHOD_STEP} />
            <RMLConnection phoneNumber={TOLL_FREE_NUMBER} />
          </div>
        </div>
      </PageWrapper>
    </div>
  );
};

export default CustomDistributionMethod;
